import { useQuery, useQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useS3Uploader } from '../useS3Uploader';
import {
  getDoctorProfileDetailsById,
  getSlotsByDateAndId,
  getSlotsDatesByRange,
  getAppointmentsByDateAndId,
  getAge,
  getRealmUserInfo
} from '../../services';
import { dateFormat, getStartAndEndOfMonth, getFormattedTime } from '../../helper';
import { selectCurrentDate, selectSelectedDate } from 'data';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

export const useDoctorDetails = (practiseUserId: string) => {
  const { generateSignedUrl } = useS3Uploader();
  const currentDate = useSelector(selectCurrentDate) || '';
  // Fallback to safeCurrentDate's date if currentDate is invalid or missing
  const safeCurrentDate =
    typeof currentDate === 'string' ? currentDate : dayjs().format(dateFormat.YYYY_MM_DD);
  const userAccessToken = localStorage.getItem('user_access_token') || '';

  const selectedDate = useSelector(selectSelectedDate) || '';

  // Ensure selectedDate is a valid string
  const safeSelectedDate = typeof selectedDate === 'string' ? selectedDate : '';

  // ===== Doctor Profile Query =====
  const doctorQuery = useQuery({
    queryKey: ['doctorDetails', practiseUserId],
    queryFn: async () => {
      const result = await getDoctorProfileDetailsById({
        practiseUserId,
        slotStartDate: safeCurrentDate,
        slotEndDate: safeCurrentDate
      });

      const basicDetails = result?.practiseBasicDetails?.[0] ?? {};
      const idDocument = result?.practiseIdDocuments?.[0] ?? {};
      const medicalDocument = result?.practiseMedicalDocuments?.[0] ?? {};
      const livePic = result?.practiseAdminLivePic?.[0] ?? {};
      const demographics = result?.practiseDemographics?.[0] ?? {};

      let doctorUser;
      try {
        doctorUser = await getRealmUserInfo(basicDetails.practiseAdminId, userAccessToken);
      } catch {
        doctorUser = {
          id: basicDetails.practiseAdminId || '',
          email: '',
          firstName: '',
          lastName: ''
        };
      }

      return {
        id: doctorUser.id,
        qrcode: result?.userPractiseMapping[0]?.qrSecret,
        role: result?.userPractiseMapping[0]?.role,
        profilePicture: await generateSignedUrl(livePic.imageContent),
        email: doctorUser.email,
        firstName: doctorUser.firstName,
        lastName: doctorUser.lastName,
        gender: basicDetails.gender,
        dob: basicDetails.DOB,
        basicDetails,
        professionalDetails: {
          ...medicalDocument,
          file: await generateSignedUrl(medicalDocument.medicalDocumentContent)
        },
        medicalDetails: {
          ...idDocument,
          file: await generateSignedUrl(idDocument.idDocumentContent)
        },
        organisationDetails: {
          ...demographics,
          file: await generateSignedUrl(demographics?.practiseFilesRelation?.fileContent)
        },
        patientCount: result?.userPractiseMapping[0]?.connectedPatients?.aggregate?.count ?? 0,
        appointmentCount:
          result?.userPractiseMapping[0]?.approvedAppointments?.aggregate?.count ?? 0,
        feedbackCount: result?.feedbacksCount?.aggregate?.count ?? 0,
        prescriptionCount: result?.prescriptionsCount?.aggregate?.count ?? 0
      };
    },
    enabled: !!practiseUserId
  });

  const { i18n } = useTranslation();
  const language = i18n.language;

  // ===== Slots & Appointments Queries =====
  const slotsQueries = useQueries({
    queries: [
      {
        queryKey: ['slotsByDate', practiseUserId, safeSelectedDate],
        queryFn: async () => {
          const result = await getSlotsByDateAndId({ practiseUserId, date: safeSelectedDate });
          return result?.practiseSlots ?? [];
        },
        enabled: !!practiseUserId && !!selectedDate
      },
      {
        queryKey: ['slotsByMonth', practiseUserId, safeCurrentDate],
        queryFn: async () => {
          const { startDate, endDate } = getStartAndEndOfMonth(safeCurrentDate);
          const result = await getSlotsDatesByRange({
            practiseUserId,
            rangeStartDate: startDate,
            rangeEndDate: endDate
          });
          return Array.from(new Set(result?.practiseSlots ?? []));
        },
        enabled: !!practiseUserId
      },
      {
        queryKey: ['boardAppointments', practiseUserId, safeSelectedDate, language],
        queryFn: async () => {
          const result = await getAppointmentsByDateAndId({
            practiseUserId,
            date: safeSelectedDate
          });

          const data = result?.Appointments ?? [];

          const doctorsList = await Promise.all(
            data.map(
              async (doctor: {
                patientUser: {
                  patientUserDocuments: { imageContent: string };
                  id: string;
                  DOB: string;
                };

                AppointmentSlotRelation: { startTime: string; endTime: string };
                practiseBasicDetailsRelation: { practiseAdminId: string };
              }) => {
                try {
                  const doctorUser = await getRealmUserInfo(
                    doctor.patientUser?.id,
                    userAccessToken
                  );

                  const profilePictureUrl = await generateSignedUrl(
                    doctor?.patientUser?.patientUserDocuments?.imageContent
                  );

                  const age = getAge(doctor?.patientUser?.DOB);
                  const startTime = getFormattedTime(
                    doctor?.AppointmentSlotRelation?.startTime,
                    language
                  );
                  const endTime = getFormattedTime(
                    doctor?.AppointmentSlotRelation?.endTime,
                    language
                  );

                  const appointment = {
                    id: doctorUser.id,
                    email: doctorUser.email,
                    time: `${startTime} - ${endTime}`,
                    patientName: `${doctorUser.firstName} ${doctorUser.lastName}`,
                    age,
                    doctorImage: profilePictureUrl
                  };

                  return appointment;
                } catch (error) {
                  console.warn(
                    `Failed to process doctor ID ${doctor.practiseBasicDetailsRelation?.practiseAdminId}:`,
                    error
                  );
                  return null;
                }
              }
            )
          );

          return {
            appointments: doctorsList.filter((d) => d !== null)
          };
        },
        enabled: !!practiseUserId
      }
    ]
  });

  const appointmentSlots = slotsQueries[0].data ?? [];
  const dates = slotsQueries[1].data;

  type AppointmentDate = { date: string };

  const uniqueDates: string[] = Array.isArray(dates)
    ? Array.from(
        new Set(
          (dates as AppointmentDate[])
            .map((item) => item.date)
            .filter((date): date is string => typeof date === 'string')
        )
      )
    : [];

  const appointmentDates = uniqueDates.map((dateStr) => ({
    fullDate: dateStr,
    day: parseInt(dateStr.split('-')[2], 10)
  }));

  const boardAppointments = slotsQueries[2].data?.appointments ?? [];

  const loading = doctorQuery.isLoading;
  const error = doctorQuery.error || slotsQueries.find((q) => q.error)?.error;

  return {
    doctorDetails: doctorQuery.data,
    appointmentSlots,
    appointmentDates,
    boardAppointments,
    loading,
    error,
    currentDate: safeCurrentDate,
    selectedDate: safeSelectedDate
  };
};
